import { useCallback, useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import CalcService from "src/services/calc-service"

const REQUEST_STATUS = {
    IDLE: "idle",
    LOADING: "loading",
    SUCCESS: "success",
    EMPTY: "empty",
    ERROR: "error",
}

const getOptions = (values) => Array.isArray(values) ? values : []

const getPeriodicities = (response) => getOptions(response?.body?.results).map((item) => ({
    value: item.PeriodicidadeNumero,
    label: item.PeriodicidadeBalanco,
}))

const getRequestStatus = (result, options) => {
    if (result.status === "rejected") return REQUEST_STATUS.ERROR

    return options.length > 0 ? REQUEST_STATUS.SUCCESS : REQUEST_STATUS.EMPTY
}

const useCompanyClassificationOptions = ({ enabled = true, sector = "" } = {}) => {
    const datalakeServiceRef = useRef(new DatalakeService())
    const calcServiceRef = useRef(new CalcService())
    const [periodicities, setPeriodicities] = useState([])
    const [sectors, setSectors] = useState([])
    const [subsectors, setSubsectors] = useState([])
    const [subsectorsSector, setSubsectorsSector] = useState("")
    const [periodicitiesStatus, setPeriodicitiesStatus] = useState(REQUEST_STATUS.IDLE)
    const [sectorsStatus, setSectorsStatus] = useState(REQUEST_STATUS.IDLE)
    const [subsectorsStatus, setSubsectorsStatus] = useState(REQUEST_STATUS.IDLE)
    const [retryVersion, setRetryVersion] = useState(0)

    const retry = useCallback(() => {
        setRetryVersion((version) => version + 1)
    }, [])

    useEffect(() => {
        if (!enabled) {
            setPeriodicities([])
            setSectors([])
            setPeriodicitiesStatus(REQUEST_STATUS.IDLE)
            setSectorsStatus(REQUEST_STATUS.IDLE)
            return undefined
        }

        let isCurrent = true
        setPeriodicitiesStatus(REQUEST_STATUS.LOADING)
        setSectorsStatus(REQUEST_STATUS.LOADING)

        const loadOptions = async () => {
            const [periodicitiesResult, sectorsResult] = await Promise.allSettled([
                Promise.resolve().then(() => datalakeServiceRef.current.getPeriodicidadeBalanco()),
                Promise.resolve().then(() => calcServiceRef.current.recuperaSetores()),
            ])

            if (!isCurrent) return

            const nextPeriodicities = periodicitiesResult.status === "fulfilled"
                ? getPeriodicities(periodicitiesResult.value)
                : []
            const nextSectors = sectorsResult.status === "fulfilled"
                ? getOptions(sectorsResult.value)
                : []

            setPeriodicities(nextPeriodicities)
            setSectors(nextSectors)
            setPeriodicitiesStatus(getRequestStatus(periodicitiesResult, nextPeriodicities))
            setSectorsStatus(getRequestStatus(sectorsResult, nextSectors))
        }

        void loadOptions()

        return () => {
            isCurrent = false
        }
    }, [enabled, retryVersion])

    useEffect(() => {
        if (!enabled || !sector) {
            setSubsectors([])
            setSubsectorsSector("")
            setSubsectorsStatus(REQUEST_STATUS.IDLE)
            return undefined
        }

        let isCurrent = true
        setSubsectors([])
        setSubsectorsSector("")
        setSubsectorsStatus(REQUEST_STATUS.LOADING)

        const loadSubsectors = async () => {
            try {
                const values = await calcServiceRef.current.recuperaSubsetores(sector)
                if (isCurrent) {
                    const nextSubsectors = getOptions(values)

                    setSubsectors(nextSubsectors)
                    setSubsectorsSector(sector)
                    setSubsectorsStatus(nextSubsectors.length > 0 ? REQUEST_STATUS.SUCCESS : REQUEST_STATUS.EMPTY)
                }
            } catch (error) {
                if (isCurrent) {
                    setSubsectors([])
                    setSubsectorsSector(sector)
                    setSubsectorsStatus(REQUEST_STATUS.ERROR)
                }
            }
        }

        void loadSubsectors()

        return () => {
            isCurrent = false
        }
    }, [enabled, retryVersion, sector])

    const hasCurrentSubsectors = enabled && Boolean(sector) && subsectorsSector === sector

    return {
        periodicities,
        sectors,
        subsectors: hasCurrentSubsectors ? subsectors : [],
        periodicitiesStatus,
        sectorsStatus,
        subsectorsStatus,
        classificationError: periodicitiesStatus === REQUEST_STATUS.ERROR || sectorsStatus === REQUEST_STATUS.ERROR,
        subsectorsError: subsectorsStatus === REQUEST_STATUS.ERROR,
        subsectorsLoading: enabled
            && Boolean(sector)
            && (subsectorsStatus === REQUEST_STATUS.LOADING || !hasCurrentSubsectors),
        retry,
    }
}

export default useCompanyClassificationOptions
